// AdminSms.jsx — Admin → SMS tab.
// Configuration + a Send Test SMS box. Bulk and per-person sending
// workflows live on the top-level Message tab (see Message.jsx).

import React, { useEffect, useState } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function AdminSms() {
    const [cfg, setCfg] = useState({ enabled: false, username: '', sender: '', passwordMasked: '', passwordSet: false, lowBalanceThreshold: 50 });
    const [cfgLoading, setCfgLoading] = useState(false);
    const [cfgMessage, setCfgMessage] = useState('');
    const [pwInput, setPwInput] = useState('');

    const [balance, setBalance] = useState(null);
    const [balanceMessage, setBalanceMessage] = useState('');

    // Send Test SMS — kept here (not on the Message tab) because it
    // pairs naturally with the configuration: tick Save → Check Balance
    // → Send Test, all in one flow to verify the credentials work.
    const [testTo, setTestTo] = useState('');
    const [testMessage, setTestMessage] = useState('Test SMS from your roster system.');
    const [testLoading, setTestLoading] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => { loadConfig(); }, []);

    async function loadConfig() {
        setCfgLoading(true);
        try {
            const res = await auditFetch(`${API_BASE}/settings/sms`);
            if (res.ok) setCfg(await res.json());
        } finally { setCfgLoading(false); }
    }

    async function handleSaveConfig() {
        setCfgMessage('Saving…');
        try {
            const body = { enabled: cfg.enabled, username: cfg.username, sender: cfg.sender, lowBalanceThreshold: cfg.lowBalanceThreshold };
            if (pwInput && pwInput.trim()) body.password = pwInput.trim();
            const res = await auditFetch(`${API_BASE}/settings/sms`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (!res.ok) { setCfgMessage('Error: ' + (data.error || res.status)); return; }
            setCfg(data);
            setPwInput('');
            setCfgMessage('Saved.');
            setTimeout(() => setCfgMessage(''), 3000);
        } catch (err) { setCfgMessage('Error: ' + err.message); }
    }

    // Show low-balance warning popup once per send when balanceAfter falls
    // below the configured threshold (returned alongside balanceAfter).
    function maybeWarnLowBalance(data) {
        if (!data || !data.ok || typeof data.balanceAfter !== 'number') return;
        const threshold = typeof data.lowBalanceThreshold === 'number' ? data.lowBalanceThreshold : 50;
        if (data.balanceAfter < threshold) {
            window.alert(`Additional SMS Credits need to be purchased ASAP. Current balance: ${data.balanceAfter}`);
        }
    }

    async function handleSendTest() {
        setTestLoading(true); setTestResult(null);
        try {
            const res = await auditFetch(`${API_BASE}/sms/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ to: testTo, message: testMessage })
            });
            const data = await res.json();
            setTestResult(data);
            // Refresh the displayed balance so it stays in sync.
            if (data?.ok && typeof data.balanceAfter === 'number') setBalance(data.balanceAfter);
            maybeWarnLowBalance(data);
        } catch (err) { setTestResult({ ok: false, error: err.message }); }
        finally { setTestLoading(false); }
    }

    async function handleCheckBalance() {
        setBalanceMessage('Checking…');
        try {
            const res = await auditFetch(`${API_BASE}/sms/balance`);
            const data = await res.json();
            if (!res.ok) { setBalance(null); setBalanceMessage('Error: ' + (data.error || res.status)); return; }
            setBalance(data.balance);
            setBalanceMessage('');
        } catch (err) { setBalanceMessage('Error: ' + err.message); }
    }

    const sectionStyle = { background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 };
    const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#444' };
    const inputStyle = { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' };
    const btnPrimary = { padding: '8px 14px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' };
    const btnSecondary = { ...btnPrimary, background: '#1565c0' };
    const btnDisabled = { ...btnPrimary, background: '#999', cursor: 'not-allowed' };

    return (
        <div className="admin-section">
            <div style={sectionStyle}>
                <h3 style={{ marginTop: 0 }}>SMS Configuration (Mobile Message)</h3>
                <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
                    Credentials live in the database, not env vars. Get the API username and password from your
                    Mobile Message account settings. The Sender ID must be a registered alphanumeric ID or a
                    verified phone number on your account. Once configured, all sending workflows are on the
                    top-level <strong>Message</strong> tab.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <label style={labelStyle}>API Username</label>
                        <input type="text" value={cfg.username} onChange={e => setCfg({ ...cfg, username: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Sender ID</label>
                        <input type="text" value={cfg.sender} onChange={e => setCfg({ ...cfg, sender: e.target.value })} placeholder="e.g. Snakepit" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>API Password</label>
                        <input
                            type="password"
                            value={pwInput}
                            onChange={e => setPwInput(e.target.value)}
                            placeholder={cfg.passwordSet ? `current: ${cfg.passwordMasked} — leave blank to keep` : 'enter password'}
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Enabled</label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 0', fontSize: 14 }}>
                            <input type="checkbox" checked={!!cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
                            <span>{cfg.enabled ? 'SMS sending is on' : 'SMS sending is off'}</span>
                        </label>
                    </div>
                    <div>
                        <label style={labelStyle}>Low-balance warning threshold (credits)</label>
                        <input
                            type="number"
                            min="0"
                            value={cfg.lowBalanceThreshold ?? ''}
                            onChange={e => setCfg({ ...cfg, lowBalanceThreshold: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                            style={inputStyle}
                        />
                        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                            Pop-up warns to top up credits whenever the post-send balance drops below this number.
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handleSaveConfig} disabled={cfgLoading} style={cfgLoading ? btnDisabled : btnPrimary}>Save</button>
                    <button onClick={handleCheckBalance} style={btnSecondary}>Check Balance</button>
                    {balance != null && <span style={{ fontSize: 14 }}>Credit balance: <strong>{balance}</strong></span>}
                    {balanceMessage && <span style={{ fontSize: 13, color: '#444' }}>{balanceMessage}</span>}
                    {cfgMessage && <span style={{ fontSize: 13, color: '#444' }}>{cfgMessage}</span>}
                </div>
            </div>

            <div style={sectionStyle}>
                <h3 style={{ marginTop: 0 }}>Send Test SMS</h3>
                <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
                    Send one SMS to verify credentials and sender ID end-to-end before using bulk features.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 12, alignItems: 'end' }}>
                    <div>
                        <label style={labelStyle}>To (Australian mobile)</label>
                        <input type="tel" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="0412345678" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Message</label>
                        <input type="text" value={testMessage} onChange={e => setTestMessage(e.target.value)} style={inputStyle} />
                    </div>
                    <button onClick={handleSendTest} disabled={testLoading || !testTo || !testMessage} style={(testLoading || !testTo || !testMessage) ? btnDisabled : btnPrimary}>
                        {testLoading ? 'Sending…' : 'Send Test'}
                    </button>
                </div>
                {testResult && (
                    <div style={{ marginTop: 8, fontSize: 13, color: testResult.ok ? '#2e7d32' : '#c62828' }}>
                        {testResult.ok
                            ? `Sent. Cost: ${testResult.totalCost || 0} credit(s). Result: ${testResult.results?.[0]?.status || 'queued'}.${typeof testResult.balanceAfter === 'number' ? ` Remaining balance: ${testResult.balanceAfter} credit(s).` : ''}`
                            : `Failed: ${testResult.error || 'unknown error'}`}
                    </div>
                )}
            </div>
        </div>
    );
}
